import { watch } from 'vue'
import { createI18n } from 'vue-i18n'
import { API_URL } from '@/utils/constants'
import en from '@/locales/en.json'
import nl from '@/locales/nl.json'

export const SUPPORTED_LOCALES = ['en', 'nl'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

const DEFAULT_LOCALE: SupportedLocale = 'en'
const STORAGE_KEY = 'locale'
const CACHE_PREFIX = 'locale_cache:'

type LocaleMessages = Record<string, unknown>

// Strings only this app renders. Anything Laravel also renders — validation,
// notification and email copy, enum labels — belongs in EMR's dictionary and
// arrives over `/locale/<locale>` instead. See CLAUDE.md § i18n.
const BUNDLED: Record<SupportedLocale, LocaleMessages> = { en, nl }

function isSupported(value: string | null | undefined): value is SupportedLocale {
  return value != null && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

function detectLocale(): SupportedLocale {
  // EMR's bilingual invitation mail opens the reset page with ?lang per button. Kept like a
  // toggle choice; bootstrap fetches the active locale's messages before mounting.
  const requested = new URLSearchParams(window.location.search).get('lang')
  if (isSupported(requested)) {
    try {
      localStorage.setItem(STORAGE_KEY, requested)
    } catch {
      // quota exceeded — the requested language still applies to this visit
    }
    return requested
  }

  const saved = localStorage.getItem(STORAGE_KEY)
  if (isSupported(saved)) return saved

  const browser = navigator.language?.split('-')[0]?.toLowerCase() ?? ''
  if (isSupported(browser)) return browser

  return DEFAULT_LOCALE
}

function readCache(locale: SupportedLocale): LocaleMessages | null {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${locale}`)
    return raw ? (JSON.parse(raw) as LocaleMessages) : null
  } catch {
    return null
  }
}

function writeCache(locale: SupportedLocale, messages: LocaleMessages): void {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${locale}`, JSON.stringify(messages))
  } catch {
    // quota exceeded — the network copy is still live for this session
  }
}

async function fetchMessages(locale: SupportedLocale): Promise<LocaleMessages> {
  const response = await fetch(`${API_URL}/locale/${locale}`)
  if (!response.ok) {
    throw new Error(`Failed to load locale "${locale}": ${response.status}`)
  }
  return (await response.json()) as LocaleMessages
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: {},
  // Keys are English source strings served by the API, so an untranslated key
  // renders as itself rather than as a missing-key warning.
  fallbackFormat: true,
  missingWarn: false,
  fallbackWarn: false,
})

// The bundled layer is the base; `loadLocaleMessages` merges the API response
// over it. Seeded here rather than through `createI18n({ messages })` so the
// plugin's message-schema inference stays keyed off the API shape, not off
// whichever strings happen to be bundled today.
for (const locale of SUPPORTED_LOCALES) {
  i18n.global.setLocaleMessage(locale, BUNDLED[locale])
}

// `<html lang>` drives screen-reader pronunciation and the browser's offer to
// translate the page. Driven from a watcher rather than set inside `setLocale`,
// so the boot path — which restores a saved locale without going through it —
// is covered by the same line.
watch(
  i18n.global.locale,
  (locale) => {
    document.documentElement.setAttribute('lang', locale)
  },
  { immediate: true },
)

// Rebuilt from BUNDLED on every call rather than merged into what is already
// loaded, so a key the API stops serving disappears instead of lingering.
function applyMessages(locale: SupportedLocale, messages: LocaleMessages): void {
  i18n.global.setLocaleMessage(locale, { ...BUNDLED[locale], ...messages })
}

export async function loadLocaleMessages(locale: SupportedLocale): Promise<void> {
  try {
    const messages = await fetchMessages(locale)
    applyMessages(locale, messages)
    writeCache(locale, messages)
  } catch (error) {
    const cached = readCache(locale)
    if (cached) {
      applyMessages(locale, cached)
      return
    }
    console.error(`[i18n] could not load locale "${locale}":`, error)
  }
}

export async function setLocale(locale: SupportedLocale): Promise<void> {
  await loadLocaleMessages(locale)
  i18n.global.locale.value = locale
  localStorage.setItem(STORAGE_KEY, locale)
}

/** The profile's saved language wins over detection; a profile with none changes nothing. */
export async function applyProfileLocale(locale: string | null | undefined): Promise<void> {
  if (!isSupported(locale) || locale === getLocale()) return
  await setLocale(locale)
}

export function getLocale(): SupportedLocale {
  return i18n.global.locale.value as SupportedLocale
}

export async function refreshAllLocales(): Promise<void> {
  await Promise.all(SUPPORTED_LOCALES.map(loadLocaleMessages))
}
